import type { MovieCard as MovieCardModel, MovieSort } from '@zyx/shared';
import { ArrowDown, ArrowDownToLine, ArrowUp, ArrowUpToLine, ListVideo, MoreHorizontal, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/page-header';
import { EmptyState, ErrorState } from '@/components/common/states';
import { AssetImage } from '@/components/movie/asset';
import { ListNameDialog, NewListDialog } from '@/components/movie/list-menu';
import { type CardMenuContext, MovieGrid, MovieGridSkeleton, MOVIE_GRID_CLASSES } from '@/components/movie/movie-grid';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useChangeList, useDeleteList, useList, useLists, useRenameList } from '@/hooks/use-library';
import { urls } from '@/lib/urls';

function count(movies: number): string {
  return `${movies.toLocaleString()} movie${movies === 1 ? '' : 's'}`;
}

/** Every custom list, by name, each with its first cover. */
export function ListsPage() {
  const lists = useLists();
  const navigate = useNavigate();
  const [naming, setNaming] = useState(false);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lists"
        description="Your own collections, in the order you arrange them. Add a movie from the list button on its cover or its page."
        actions={
          <Button onClick={() => setNaming(true)}>
            <Plus />
            New list
          </Button>
        }
      />
      {lists.isPending ? (
        <MovieGridSkeleton count={6} />
      ) : lists.isError ? (
        <ErrorState error={lists.error} onRetry={() => void lists.refetch()} />
      ) : lists.data.lists.length === 0 ? (
        <EmptyState title="No lists yet" description="Make one here, or from the list button on any movie." icon={<ListVideo className="size-5" />} />
      ) : (
        <div className={MOVIE_GRID_CLASSES}>
          {lists.data.lists.map((list) => (
            <Link key={list.listId} to={urls.list(list.listId)} className="group space-y-2 focus-visible:outline-none">
              <div className="overflow-hidden rounded-lg ring-offset-background group-hover:ring-2 group-hover:ring-primary group-focus-visible:ring-2 group-focus-visible:ring-ring">
                <AssetImage file={list.cover} alt={list.name} className="aspect-[3/2] w-full" />
              </div>
              <div>
                <p className="truncate text-sm font-medium">{list.name}</p>
                <p className="text-xs text-muted-foreground">{count(list.movieCount)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
      <NewListDialog open={naming} onOpenChange={setNaming} onCreated={(list) => navigate(urls.list(list.listId))} />
    </div>
  );
}

/**
 * Moving a movie within its list, by naming the movie it should follow. Moves reach only
 * as far as what is loaded: "to the end" needs the end on screen.
 */
function MoveMenu(props: { readonly listId: number; readonly movie: MovieCardModel; readonly context: CardMenuContext }) {
  const change = useChangeList();
  const { index, loaded, hasMore } = props.context;
  const code = props.movie.code;
  const move = (after: string | null) => change.mutate({ listId: props.listId, code, on: true, after });
  const last = loaded[loaded.length - 1];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Arrange"
          title="Arrange"
          className="rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover/card:opacity-100 group-focus-within/card:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreHorizontal className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled={index === 0} onSelect={() => move(null)}>
          <ArrowUpToLine />
          Move to the top
        </DropdownMenuItem>
        <DropdownMenuItem disabled={index === 0} onSelect={() => move(index >= 2 ? loaded[index - 2].code : null)}>
          <ArrowUp />
          Move up
        </DropdownMenuItem>
        <DropdownMenuItem disabled={index >= loaded.length - 1} onSelect={() => move(loaded[index + 1].code)}>
          <ArrowDown />
          Move down
        </DropdownMenuItem>
        <DropdownMenuItem disabled={hasMore || index >= loaded.length - 1} onSelect={() => move(last.code)}>
          <ArrowDownToLine />
          Move to the end
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => change.mutate({ listId: props.listId, code, on: false })}>
          <X />
          Remove from list
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const LIST_SORTS: ReadonlyArray<readonly [MovieSort, string]> = [
  ['list', 'Your order'],
  ['published', 'Newest'],
];

export function ListPage() {
  const listId = Number(useParams().listId);
  // Keyed, so moving to another list starts with its own order rather than this one's.
  return <ListView key={listId} listId={listId} />;
}

function ListView(props: { readonly listId: number }) {
  const { listId } = props;
  const list = useList(listId);
  const navigate = useNavigate();
  const rename = useRenameList(listId);
  const remove = useDeleteList(listId, () => navigate(urls.lists()));
  const [sort, setSort] = useState<MovieSort>('list');
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (list.isError) {
    return <ErrorState error={list.error} />;
  }
  const found = list.data?.list;
  return (
    <div className="space-y-6">
      <PageHeader
        title={found?.name ?? '…'}
        description={
          <>
            <Link to={urls.lists()} className="hover:text-foreground hover:underline">
              Lists
            </Link>
            {found === undefined ? null : ` · ${count(found.movieCount)}`}
          </>
        }
        actions={
          <>
            <Select value={sort} onValueChange={(value) => setSort(LIST_SORTS.find(([candidate]) => candidate === value)?.[0] ?? 'list')}>
              <SelectTrigger className="w-36" aria-label="Order">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LIST_SORTS.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setRenaming(true)} disabled={found === undefined}>
              <Pencil />
              Rename
            </Button>
            <Button variant="ghost" onClick={() => setDeleting(true)} disabled={found === undefined}>
              <Trash2 />
              Delete
            </Button>
          </>
        }
      />
      <MovieGrid
        request={{ listId, sort }}
        empty={{ title: 'Nothing on this list yet', description: 'Use the list button on any movie to add it here.' }}
        cardMenu={sort === 'list' ? (movie, context) => <MoveMenu listId={listId} movie={movie} context={context} /> : undefined}
      />
      <ListNameDialog
        open={renaming}
        onOpenChange={setRenaming}
        title="Rename list"
        initial={found?.name}
        submitLabel="Rename"
        pending={rename.isPending}
        onSubmit={(name) => rename.mutateAsync(name)}
      />
      <AlertDialog open={deleting} onOpenChange={setDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{found?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>The list goes; its movies stay in your library, along with their marks and progress.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate()}>Delete list</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
